<template>
  <div class="wildfire-farm-report">
    <!-- Meta strip -->
    <div class="wildfire-farm-report__meta-strip d-flex flex-wrap align-center justify-space-between">
      <div class="d-flex flex-wrap wildfire-farm-report__meta-list">
        <div v-if="region || country" class="wildfire-farm-report__meta-item">
          <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
          {{ [region, country].filter(Boolean).join(', ') }}
        </div>
        <div v-if="geometryLabel" class="wildfire-farm-report__meta-item">
          <v-icon x-small class="mr-1">{{ geometryIcon }}</v-icon>
          {{ geometryLabel }}
        </div>
        <div v-if="generatedAt" class="wildfire-farm-report__meta-item">
          <v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>
          {{ generatedOnLabel }}: {{ generatedAt }}
        </div>
      </div>
      <div v-if="riskLegend && riskLegend.length" class="wildfire-farm-report__legend-strip">
        <span v-for="item in riskLegend" :key="item.label" class="wildfire-farm-report__legend-chip">
          <span class="wildfire-farm-report__legend-swatch" :style="{ background: item.color }"></span>
          {{ item.label }}
        </span>
      </div>
    </div>

    <!-- Interactive farm map -->
    <WildfireFarmMap
      class="wildfire-farm-report__map"
      :coordinates="coordinates"
      :center-lat="centerLat"
      :center-lng="centerLng"
      :radius-km="radiusKm"
      :bbox="bbox"
      :fill-color="highestRiskTier ? highestRiskTier.color : '#FFA826'"
      :opacity-label="overlayOpacityLabel"
    />

    <!-- Summary cards -->
    <div class="wildfire-farm-report__summary-cards">
      <div class="wildfire-farm-report__summary-card">
        <div class="wildfire-farm-report__summary-label">{{ highestRiskLabel }}</div>
        <div v-if="highestRiskTier" class="wildfire-farm-report__summary-value d-flex align-center">
          <span class="wildfire-farm-report__legend-swatch wildfire-farm-report__legend-swatch--lg" :style="{ background: highestRiskTier.color }"></span>
          {{ highestRiskTier.label }}
        </div>
        <div v-else class="wildfire-farm-report__summary-value">—</div>
      </div>
      <div class="wildfire-farm-report__summary-card">
        <div class="wildfire-farm-report__summary-label">{{ areaLabel }}</div>
        <div class="wildfire-farm-report__summary-value">{{ formattedArea }} ha</div>
      </div>
      <div class="wildfire-farm-report__summary-card">
        <div class="wildfire-farm-report__summary-label">{{ modelLabel }}</div>
        <div class="wildfire-farm-report__summary-value">{{ formattedModel || '—' }}</div>
      </div>
    </div>

    <!-- Risk breakdown: table + donut -->
    <div v-if="riskLevels && riskLevels.length" class="wildfire-farm-report__section">
      <div class="wildfire-farm-report__section-title">{{ riskBreakdownTitle }}</div>
      <div class="wildfire-farm-report__breakdown">
        <table class="wildfire-farm-report__table">
          <thead>
            <tr>
              <th>{{ riskLevelColumnLabel }}</th>
              <th class="text-right">{{ areaColumnLabel }}</th>
              <th class="text-right">{{ shareColumnLabel }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tier in riskLevels" :key="tier.label">
              <td>
                <span class="wildfire-farm-report__legend-swatch" :style="{ background: tier.color }"></span>
                {{ tier.label }}
              </td>
              <td class="text-right">{{ formatHectares(tier.areaHectares) }}</td>
              <td class="text-right font-weight-bold" :style="{ color: tier.color }">{{ tier.percentage.toFixed(1) }}%</td>
            </tr>
          </tbody>
        </table>

        <div class="wildfire-farm-report__donut-wrap">
          <svg viewBox="0 0 100 100" class="wildfire-farm-report__donut">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#eceff1" stroke-width="16" />
            <circle
              v-for="(seg, i) in donutSegments"
              :key="i"
              cx="50" cy="50" r="40" fill="none"
              :stroke="seg.color"
              stroke-width="16"
              :stroke-dasharray="seg.dasharray"
              :stroke-dashoffset="seg.dashoffset"
              transform="rotate(-90 50 50)"
            />
          </svg>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import WildfireFarmMap from './WildfireFarmMap.vue'

const SEVERITY_RANK = { high: 3, medium: 2, compliant: 1, low: 1 }

export default {
  name: 'WildfireFarmReport',
  components: { WildfireFarmMap },
  props: {
    riskLevels: { type: Array, default: () => [] },
    riskLegend: { type: Array, default: () => [] },
    farmAreaHectares: { type: Number, default: null },
    region: { type: String, default: null },
    country: { type: String, default: null },
    model: { type: String, default: null },
    geometryType: { type: String, default: null },
    coordinates: { type: Array, default: () => [] },
    centerLat: { type: Number, default: null },
    centerLng: { type: Number, default: null },
    radiusKm: { type: Number, default: null },
    bbox: { type: Array, default: null },
    generatedAt: { type: String, default: null },
    areaLabel: { type: String, default: 'Farm Area' },
    modelLabel: { type: String, default: 'Model' },
    generatedOnLabel: { type: String, default: 'Generated' },
    riskBreakdownTitle: { type: String, default: 'Risk Breakdown by Area' },
    highestRiskLabel: { type: String, default: 'Highest Risk' },
    riskLevelColumnLabel: { type: String, default: 'Risk Level' },
    areaColumnLabel: { type: String, default: 'Area (ha)' },
    shareColumnLabel: { type: String, default: 'Share' },
    overlayOpacityLabel: { type: String, default: 'Overlay opacity' },
    polygonBoundaryLabel: { type: String, default: 'Polygon Boundary' },
    circularAreaLabel: { type: String, default: 'Circular Area' },
  },
  computed: {
    formattedArea() {
      return this.formatHectares(this.farmAreaHectares)
    },
    formattedModel() {
      if (!this.model) return ''
      return this.model.charAt(0).toUpperCase() + this.model.slice(1).replace(/_/g, ' ')
    },
    geometryLabel() {
      const type = String(this.geometryType || '').toLowerCase()
      if (type.startsWith('poly')) return this.polygonBoundaryLabel
      if (type.startsWith('circ')) return this.circularAreaLabel
      return ''
    },
    geometryIcon() {
      const type = String(this.geometryType || '').toLowerCase()
      if (type.startsWith('poly')) return 'mdi-vector-polygon'
      if (type.startsWith('circ')) return 'mdi-vector-circle'
      return 'mdi-shape-outline'
    },
    // The most severe risk tier that actually covers part of the farm — falls back to
    // the most severe tier overall if every tier reads as ~0% (e.g. no data yet).
    highestRiskTier() {
      const levels = this.riskLevels || []
      if (!levels.length) return null
      const withArea = levels.filter(t => Number(t.percentage) > 0.05)
      const pool = withArea.length ? withArea : levels
      return pool.reduce((best, tier) => {
        const rank = SEVERITY_RANK[String(tier.label).toLowerCase()] || 0
        const bestRank = best ? (SEVERITY_RANK[String(best.label).toLowerCase()] || 0) : -1
        return rank > bestRank ? tier : best
      }, null)
    },
    donutSegments() {
      const circumference = 2 * Math.PI * 40
      let cumulative = 0
      return (this.riskLevels || [])
        .filter(tier => Number(tier.percentage) > 0.05)
        .map(tier => {
          const dash = (Number(tier.percentage) / 100) * circumference
          const seg = {
            color: tier.color,
            dasharray: `${dash} ${circumference - dash}`,
            dashoffset: -cumulative,
          }
          cumulative += dash
          return seg
        })
    },
  },
  methods: {
    formatHectares(value) {
      if (value == null || Number.isNaN(Number(value))) return '—'
      return Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })
    },
  },
}
</script>

<style scoped>
.wildfire-farm-report__meta-strip {
  gap: 6px 12px;
  margin-bottom: 10px;
}

.wildfire-farm-report__meta-list {
  gap: 6px 16px;
}

.wildfire-farm-report__meta-item {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #607D8B;
}

.wildfire-farm-report__legend-strip {
  display: flex;
  gap: 10px;
}

.wildfire-farm-report__legend-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: #607D8B;
}

.wildfire-farm-report__map {
  margin-bottom: 14px;
}

.wildfire-farm-report__summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.wildfire-farm-report__summary-card {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 10px 14px;
  background: #fff;
}

.wildfire-farm-report__summary-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #90A4AE;
  margin-bottom: 4px;
}

.wildfire-farm-report__summary-value {
  font-size: 18px;
  font-weight: 700;
  color: #37474F;
}

.wildfire-farm-report__section {
  margin-bottom: 14px;
}

.wildfire-farm-report__section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #607D8B;
  margin-bottom: 8px;
}

.wildfire-farm-report__breakdown {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.wildfire-farm-report__table {
  flex: 1 1 240px;
  border-collapse: collapse;
  font-size: 13px;
}

.wildfire-farm-report__table th {
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #90A4AE;
  padding: 4px 8px;
  border-bottom: 1px solid #eceff1;
}

.wildfire-farm-report__table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f5f5f5;
  color: #37474F;
}

.wildfire-farm-report__donut-wrap {
  flex: 0 0 auto;
  width: 110px;
}

.wildfire-farm-report__donut {
  width: 100%;
  height: auto;
}

.wildfire-farm-report__legend-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 6px;
  flex-shrink: 0;
}

.wildfire-farm-report__legend-swatch--lg {
  width: 14px;
  height: 14px;
  border-radius: 3px;
}
</style>
